"use client"

import { createRoute } from "@tanstack/react-router"
import { useQueryClient } from "@tanstack/react-query"
import { Plus } from "lucide-react"
import { toast } from "sonner"

import { PageAction, PageHeader } from "@/components/graphite/page-header"
import { GraphiteJobs, JobTabs } from "@/components/jobs/graphite-jobs"
import {
  JobConfigurationDialog,
  type JobConfigurationData,
} from "@/components/job-configuration-dialog"
import { useJobs } from "@/hooks/use-jobs"
import MainAppRoute from "@/rootRoutes/MainApp"
import { usePostJobs } from "openapi/api/endpoints"

/**
 * Jobs.
 *
 * The page owns the create dialog; everything else comes from `useJobs` and is
 * rendered by `GraphiteJobs`. There is no artboard for this screen — turn 9's
 * console is a single job with the Jobs tab lit — so it is built as 5a's
 * table, which is the same kind of screen.
 */
function JobsPage() {
  const jobs = useJobs()
  const queryClient = useQueryClient()
  const { mutateAsync: createJob, isPending: isCreating } = usePostJobs()

  /*
    The wizard collected everything and threw it away: it calls `onJobCreated`
    and nothing else, no call site supplied one, and nothing in the app called
    `POST /jobs` at all — so a completed New Job wizard silently discarded the
    job. Codex caught the missing callback; the wider hole was that creating a
    job did not work anywhere in the product.

    Hiring managers ride along as `contacts`, which is the shape the endpoint
    takes them in.
  */
  const handleCreate = async (job: JobConfigurationData) => {
    if (!job.company_id) {
      toast.error("Pick a company first", {
        description: "A job belongs to a company, and the API will not take it without one.",
      })
      throw new Error("A job needs a company")
    }

    try {
      await createJob({
        data: {
          title: job.title,
          company_id: job.company_id,
          location: job.location || null,
          department: job.department || null,
          type: job.type || null,
          salary_range: job.salary_range || null,
          description: job.description || undefined,
          associated_search_id: job.selectedSearch?.id ?? undefined,
          contacts: job.hiringManagers.map((manager) => {
            const [first, ...rest] = (manager.name ?? "").trim().split(/\s+/)
            return {
              first_name: first || undefined,
              last_name: rest.join(" ") || undefined,
              email: manager.email || undefined,
              title: manager.position || null,
              contact_type: "hiring_manager",
            }
          }),
        },
      })

      await queryClient.invalidateQueries({ queryKey: ["/jobs"], exact: false })
      toast.success(`${job.title} created`)
    } catch (error) {
      toast.error("Could not create the job", { description: `${error}` })
      /*
        Rethrown so the wizard keeps the form. Swallowing it here would look
        like success to the dialog, which would then clear a completed form
        the API had just refused.
      */
      throw error
    }
  }

  return (
    <div className="@container flex h-full min-h-0 flex-col">
      <PageHeader
        title="Jobs"
        description="Every role you are hiring for"
        meta={jobs.isCounted ? `${jobs.total} jobs` : undefined}
        actions={
          /* The pill, per canvas turn 33 — one per page, and this is the job
             this page exists to do. */
          <JobConfigurationDialog
            onJobCreated={handleCreate}
            trigger={
              <PageAction tone="primary">
                <Plus className="size-[15px]" />
                {isCreating ? "Creating…" : "New Job"}
              </PageAction>
            }
          />
        }
      >
        <JobTabs
          tab={jobs.tab}
          counts={jobs.isCounted ? jobs.counts : undefined}
          onTabChange={jobs.setTab}
        />
      </PageHeader>

      <GraphiteJobs
        rows={jobs.rows}
        locations={jobs.locations}
        search={jobs.search}
        onSearchChange={jobs.setSearch}
        location={jobs.location}
        onLocationChange={jobs.setLocation}
        total={jobs.total}
        isLoading={jobs.isLoading}
        isError={jobs.isError}
      />
    </div>
  )
}

const JobsPageRoute = createRoute({
  getParentRoute: () => MainAppRoute,
  path: "/jobs",
  component: () => <JobsPage />,
})

export default JobsPageRoute
